/**
 * useWompiWidget.js  —  v4.0
 *
 * Full Wompi checkout flow:
 *
 *   1. POST /prepare-checkout?subscription_pricing_id=<id>
 *      → { reference, amount_in_cents, currency, integrity_hash, pricing_id }
 *      → 400 + error_code "0018" → user already active → onAlreadySubscribed called
 *      (For non-card upgrades, upgradeMode=true appends &upgrade_to_pricing_id=<id>
 *       so backend skips the Active-subscription block.)
 *
 *   2. Open Wompi Widget (programmatic JS API — requires a callback function).
 *      The Widget handles all payment methods: card, PSE, Nequi, etc.
 *
 *   3. In the callback:
 *      - If APPROVED: extract payment_source_id ONLY if present (card payments).
 *        PSE/Nequi do NOT return payment_source_id — do not error on its absence.
 *      - Call POST /confirm-checkout (with payment_source_id only when present).
 *      - Poll GET /active-subscription until Active.
 *      - Call onSuccess().
 *
 * Usage:
 *   const { openWompiWidget, widgetState, errorMsg, reset } = useWompiWidget({ onSuccess, onError, onAlreadySubscribed });
 *   openWompiWidget({ pricingId });
 *   openWompiWidget({ pricingId, upgradeMode: true }); // non-card upgrade
 */

import { useState, useCallback, useRef } from "react";
import { makeRequest } from "../assets/functions_helper";
import { waitForActivation } from "./useActiveSubscription";

// ─── Wompi public key ──────────────────────────────────────────────────────────
// Reads from .env (REACT_APP_WOMPI_PUBLIC_KEY).
// Sanitize the environment variable. It must be a valid public key starting with "pub_".
let rawKey = process.env.REACT_APP_WOMPI_PUBLIC_KEY;
if (typeof rawKey !== "string" || !rawKey.startsWith("pub_")) {
  rawKey = "pub_prod_FVur7K1qbeI4KeyovByw46DiBvNxSjRv";
}
const WOMPI_PUBLIC_KEY = rawKey;

const WOMPI_CDN = "https://checkout.wompi.co/widget.js";

// ─── Widget states ─────────────────────────────────────────────────────────────
export const WIDGET_STATE = {
  IDLE:       "idle",       // Nothing happening
  PREPARING:  "preparing",  // POST /prepare-checkout in-flight
  LOADING:    "loading",    // Loading CDN script
  OPEN:       "open",       // Widget is open, user choosing payment method
  CONFIRMING: "confirming", // POST /confirm-checkout in-flight
  POLLING:    "polling",    // Polling /active-subscription
  SUCCESS:    "success",    // Active confirmed
  ERROR:      "error",      // Something went wrong
};

// ─── Script loader ─────────────────────────────────────────────────────────────
function loadWompiScript() {
  return new Promise((resolve, reject) => {
    // Force fresh widget load — avoids stale cached instance with wrong key
    const existingScript = document.querySelector(`script[src="${WOMPI_CDN}"]`);
    if (existingScript) {
      existingScript.remove();
      delete window.WidgetCheckout;
    }
    const script = document.createElement("script");
    script.src   = WOMPI_CDN;
    script.async = true;
    script.setAttribute("data-public-key", WOMPI_PUBLIC_KEY);
    script.onload  = resolve;
    script.onerror = () =>
      reject(new Error("No se pudo cargar el módulo de pago. Revisa tu conexión."));
    document.head.appendChild(script);
  });
}

function getEmailFromToken() {
  try {
    const token = localStorage.getItem("token");
    if (!token) return null;
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    const parsed = JSON.parse(jsonPayload);
    // Usually the email is in sub or email
    return parsed.email || parsed.sub || null;
  } catch (e) {
    return null;
  }
}

// ─── Hook ──────────────────────────────────────────────────────────────────────
/**
 * @param {object}   options
 * @param {function} options.onSuccess           — called after Active confirmed
 * @param {function} options.onError             — called with an error message string
 * @param {function} options.onAlreadySubscribed — called when backend returns error_code "0018"
 *                                                 and upgradeMode is false
 */
export function useWompiWidget({ onSuccess, onError, onAlreadySubscribed } = {}) {
  const [widgetState, setWidgetState] = useState(WIDGET_STATE.IDLE);
  const [errorMsg, setErrorMsg]       = useState(null);
  const abortRef = useRef(false);

  /**
   * @param {object}  params
   * @param {string|number} params.pricingId   — subscription_pricing_id selected by user
   * @param {boolean} [params.upgradeMode]     — true for non-card upgrades via Widget.
   *                                             Appends &upgrade_to_pricing_id= to prepare-checkout
   *                                             so the backend skips the Active-subscription block.
   */
  const openWompiWidget = useCallback(
    async ({ pricingId, upgradeMode = false, paymentMethod = "CARD" } = {}) => {
      if (!pricingId) {
        console.error("[useWompiWidget] pricingId is required.");
        return;
      }

      abortRef.current = false;
      setErrorMsg(null);
      setWidgetState(WIDGET_STATE.PREPARING);

      const token     = localStorage.getItem("token");
      const tokenType = localStorage.getItem("tokenType");
      const email     = getEmailFromToken();

      // ── 1. Prepare checkout ─────────────────────────────────────────────────
      const upgradeParam = upgradeMode ? `&upgrade_to_pricing_id=${pricingId}` : "";
      let prepareData;
      try {
        prepareData = await makeRequest(
          "POST",
          `subscriptions_router/prepare-checkout?subscription_pricing_id=${pricingId}${upgradeParam}`,
          null,
          "application/json",
          token,
          tokenType,
          null,
          true // skipLoader
        );
      } catch (err) {
        if (abortRef.current) return;

        // error_code "0018" → already active → only treat as error if not in upgrade mode
        const raw = typeof err === "string" ? err : err?.message || JSON.stringify(err);
        const isAlreadySubscribed =
          raw.includes("0018") ||
          (err?.error_code && String(err.error_code) === "0018");

        if (isAlreadySubscribed && !upgradeMode) {
          console.warn("[useWompiWidget] error_code 0018 — user already subscribed.");
          setWidgetState(WIDGET_STATE.IDLE);
          if (typeof onAlreadySubscribed === "function") onAlreadySubscribed();
          return;
        }

        const msg = err?.message || "No se pudo iniciar el proceso de pago. Inténtalo de nuevo.";
        setErrorMsg(msg);
        setWidgetState(WIDGET_STATE.ERROR);
        if (typeof onError === "function") onError(msg);
        return;
      }

      if (abortRef.current) return;

      const {
        reference,
        amount_in_cents: amountInCents,
        integrity_hash,
        currency = "COP",
        pricing_id: confirmedPricingId,
      } = prepareData || {};

      const resolvedPricingId = confirmedPricingId || pricingId;

      if (!reference || !amountInCents || !integrity_hash) {
        const msg = "La respuesta del servidor es inválida. Contacta a soporte.";
        setErrorMsg(msg);
        setWidgetState(WIDGET_STATE.ERROR);
        if (typeof onError === "function") onError(msg);
        return;
      }

      // ── 2. Load Wompi script ────────────────────────────────────────────────
      setWidgetState(WIDGET_STATE.LOADING);
      try {
        await loadWompiScript();
      } catch (err) {
        if (abortRef.current) return;
        const msg = err?.message || "Error al cargar el módulo de pago.";
        setErrorMsg(msg);
        setWidgetState(WIDGET_STATE.ERROR);
        if (typeof onError === "function") onError(msg);
        return;
      }

      if (abortRef.current) return;
      setWidgetState(WIDGET_STATE.OPEN);

      // ── 3. Open Wompi Widget ────────────────────────────────────────────────
      // Save the pricing ID so the callback page can read it after redirect
      localStorage.setItem("pending_subscription_pricing_id", resolvedPricingId);

      console.log("[useWompiWidget] publicKey:", WOMPI_PUBLIC_KEY, "paymentMethod:", paymentMethod);
      const isCard = paymentMethod === "CARD";

      const checkoutOptions = {
        currency: currency,
        amountInCents: amountInCents,
        reference: reference,
        signature: { integrity: integrity_hash },
        publicKey: WOMPI_PUBLIC_KEY,
        widgetOperation: isCard ? "tokenize" : "purchase",
        ...(email ? { customerData: { email } } : {}),
      };

      const checkout = new window.WidgetCheckout(checkoutOptions);

      checkout.open(async (result) => {
        if (abortRef.current) return;

        let paymentMethodType = paymentMethod;
        let paymentToken = null;

        if (isCard) {
          // Tokenization mode
          const paymentSource = result?.payment_source || result?.data?.payment_source;
          if (!paymentSource) {
             setWidgetState(WIDGET_STATE.IDLE);
             return; // user closed widget or error
          }
          // The token can be in paymentSource.data.id or paymentSource.id
          paymentToken = paymentSource.data?.id || paymentSource.id || paymentSource.token;
        } else {
          // Standard Payment mode
          const transaction = result?.transaction;
          if (!transaction || transaction.status !== "APPROVED") {
            setWidgetState(WIDGET_STATE.IDLE);
            return;
          }
          paymentMethodType = transaction.payment_method?.type || paymentMethodType;
          paymentToken = transaction.payment_method?.token;
        }

        // ── 5. Confirm checkout ───────────────────────────────────────────────
        setWidgetState(WIDGET_STATE.CONFIRMING);
        try {
          if (paymentMethodType === "CARD" && paymentToken) {
            // Call the card endpoint
            await makeRequest(
              "POST",
              `subscriptions_router/confirm-checkout-card?subscription_pricing_id=${resolvedPricingId}&card_token=${paymentToken}&reference=${reference}`,
              null,
              "application/json",
              token,
              tokenType,
              null,
              true // skipLoader
            );
          } else {
            // Call the non-card endpoint
            await makeRequest(
              "POST",
              `subscriptions_router/confirm-checkout?subscription_pricing_id=${resolvedPricingId}&reference=${reference}`,
              null,
              "application/json",
              token,
              tokenType,
              null,
              true // skipLoader
            );
          }
        } catch (err) {
          // Non-fatal — webhook activates the subscription regardless.
          console.warn("[useWompiWidget] confirm-checkout failed (non-fatal), continuing to poll.", err);
        }

        if (abortRef.current) return;

        // ── 6. Poll for activation ────────────────────────────────────────────
        setWidgetState(WIDGET_STATE.POLLING);
        try {
          await waitForActivation({ maxAttempts: 40, intervalMs: 3000 });
        } catch (_) {
          // Timeout — webhook may still fire later; proceed to onSuccess
          // so the user is not left stuck on the plans page.
        }

        if (abortRef.current) return;

        // ── 7. Done ───────────────────────────────────────────────────────────
        setWidgetState(WIDGET_STATE.SUCCESS);
        if (typeof onSuccess === "function") onSuccess();
      });
    },
    [onSuccess, onError, onAlreadySubscribed]
  );

  const reset = useCallback(() => {
    abortRef.current = true;
    setWidgetState(WIDGET_STATE.IDLE);
    setErrorMsg(null);
  }, []);

  return { openWompiWidget, widgetState, errorMsg, reset };
}
